import { Check } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import type { PersonnelDetail, PersonnelEligibility } from '../personnelTypes';

/**
 * Civil service eligibilities earned — CS Form 212, section IV.
 *
 * Reads down a column like the service record and the education history: what they hold, what they
 * scored, when and where. A card per eligibility would make that a scroll.
 */
export function EligibilityPanel({
  record,
  onAdd,
  onEdit,
  onDelete,
  onBulkDelete,
  onRestore,
}: {
  record: PersonnelDetail;
  onAdd: () => void;
  onEdit: (row: PersonnelEligibility) => void;
  onDelete: (row: PersonnelEligibility) => void;
  onBulkDelete: (rows: PersonnelEligibility[]) => void;
  onRestore: (row: PersonnelEligibility) => void;
}) {

  const [showDeleted, setShowDeleted] = useState(false);

  const rows = useMemo(
    () => (showDeleted ? record.eligibilities : record.eligibilities.filter((x) => x.isActive)),
    [record.eligibilities, showDeleted],
  );

  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? rows.filter((x) =>
            [x.eligibilityName, x.placeOfExamination, x.licenseNumber]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
          )
        : rows;

      const field = query.sort?.replace(/^-/, '') ?? 'eligibilityName';
      const descending = query.sort?.startsWith('-') ?? false;

      const sorted = [...matched].sort((a, b) => {
        if (field === 'rating') {
          /*
            A rating is a NUMBER, so it compares as one — string order would put 9 above 85.
            Rows without a rating sort last whichever way the column is pointed: they are
            eligibilities that were conferred rather than examined for, and burying them among
            the scores would read as a score of nothing.
          */
          const av = a.rating ?? null;
          const bv = b.rating ?? null;
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return (av - bv) * (descending ? -1 : 1);
        }

        const pick = (row: PersonnelEligibility) =>
          field === 'dateOfExamination'
            ? row.dateOfExamination
            : field === 'placeOfExamination'
              ? (row.placeOfExamination ?? '')
              : row.eligibilityName;

        return String(pick(a)).localeCompare(String(pick(b))) * (descending ? -1 : 1);
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [rows],
  );

  const state = useDataTable<PersonnelEligibility>({
    fetchPage,
    pageSize: 10,

    // By name: there is no rank among eligibilities, which is why the catalog orders by name too.
    defaultSort: 'eligibilityName',
  });

  const columns: DataColumn<PersonnelEligibility>[] = [
    {
      key: 'eligibilityName',
      header: 'Eligibility',
      width: 260,
      sortable: true,
      render: (x) => <TextCell value={x.eligibilityName} />,
    },
    {
      key: 'rating',
      header: 'Rating',
      width: 110,
      sortable: true,
      /*
        `numeric` AND `mono`: a rating is a quantity, so a zero dims like every other zero in the
        product. Two decimals always, because 85 and 85.00 are the same score and a column that
        renders them differently cannot be scanned.
      */
      render: (x) => (
        <TextCell
          value={x.rating === null || x.rating === undefined ? null : x.rating.toFixed(2)}
          mono
          numeric
        />
      ),
    },
    {
      // A calendar date — the day an examination was sat, not an instant.
      key: 'dateOfExamination',
      header: 'Examined',
      width: 140,
      sortable: true,
      render: (x) => <TextCell value={formatDateOnly(x.dateOfExamination)} mono />,
    },
    {
      key: 'placeOfExamination',
      header: 'Place',
      width: 200,
      sortable: true,
      render: (x) => <TextCell value={x.placeOfExamination} />,
    },
    {
      key: 'licenseNumber',
      header: 'Licence no.',
      width: 150,
      // A code, so mono. Blank for eligibilities that entail no licence — the form never asked.
      render: (x) => <TextCell value={x.licenseNumber} mono />,
    },
    {
      key: 'licenseValidUntil',
      header: 'Valid until',
      width: 140,
      /*
        The one date on this record that points FORWARD, and the only column here a reader
        scans for a problem: a licence whose validity has passed. It is not tinted red — status
        tones belong to a record's own state, and a lapsed licence is a fact about a date — but
        it is the reason this column exists rather than living in the row's Other Info.
      */
      render: (x) => (
        <TextCell value={x.licenseValidUntil ? formatDateOnly(x.licenseValidUntil) : null} mono />
      ),
    },
    {
      /*
        A panel whose rows can be deleted owes a status column: without one a deleted row
        carries no marker while Show deleted is on, which is the one state a row must
        never be silent about. This panel offered Show deleted and had no column to show
        it in. `<DataTablePanel>` moves it to the end of the data columns itself.
      */
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (x) =>
        x.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];

  return (
    <ProfileSection>
      <DataTablePanel<PersonnelEligibility>
        id="personnel-eligibility"
        state={state}
        /*
          The first column opens the row as a key/value preview. A table row compares records;
          it is a poor way to read ONE, because the columns are spread across a scroll and the
          narrow ones truncate. The preview is built from these same `columns`, so it cannot
          drift from the table or miss one somebody adds.
        */
        // The size of the modal that EDITS this record, so reading a row and correcting it
        // happen at one width rather than the dialog changing shape between them.
        rowPreview={{ title: 'Eligibility', size: '2xl' }}
        columns={columns}
        rowKey={(x) => x.id}
        searchPlaceholder="Search eligibility, place or licence"
        onCreate={onAdd}
        createPermission="personnel.update"
        createDisabledReason={
          record.isActive
            ? null
            : 'This employee is deleted. Restore them before editing the record.'
        }
        /*
          The kebab's entries. The shell owns the gutter, the menu and the right-click
          shortcut — this is only what goes IN the menu, and the same array feeds the table
          and the list, so the two cannot offer different actions on one row.

          Delete and Restore are the two halves of one state, so only the applicable one is
          offered. That is not the disable-for-state case: "restore a live row" is not
          something the reader is part-way through, it is simply not an action here.
        */
        rowActions={(x) => [
          {
            key: `edit-${x.id}`,
            label: 'Edit',
            permission: 'personnel.update',
            disabledReason: x.isActive
              ? null
              : 'This record is deleted. Restore it before editing.',
            onClick: () => onEdit(x),
          },
          x.isActive
            ? {
                key: `delete-${x.id}`,
                label: 'Delete',
                permission: 'personnel.update',
                destructive: true,
                onClick: () => onDelete(x),
              }
            : {
                key: `restore-${x.id}`,
                label: 'Restore',
                permission: 'personnel.update',
                onClick: () => onRestore(x),
              },
        ]}
        otherInfo={(x) => ({
          createdAt: x.createdAt,
          createdByName: x.createdByName,
          updatedAt: x.updatedAt,
          updatedByName: x.updatedByName,
        })}
        /*
          The checkbox gutter is ON, which is the default. It carried `selectable={false}`,
          and that reading is the one CLAUDE.md names: the opt-out is only for a table nothing
          can ever be done to, never for one whose bulk action is merely unbuilt. Every row
          here can be deleted one at a time, so the column belongs — and the bulk action ships
          with it rather than leaving a selection with nowhere to go.
        */
        bulkActions={(selected) => {
          const deletable = selected.filter((x) => x.isActive);

          return [
            {
              key: 'delete-eligibilities',
              label: `Delete ${deletable.length} eligibility record${deletable.length === 1 ? '' : 's'}`,
              permission: 'personnel.update',
              destructive: true,
              disabledReason: !record.isActive
                ? 'This employee is deleted. Restore them before editing the record.'
                : deletable.length === 0
                  ? 'Every selected eligibility record is already deleted.'
                  : null,
              onClick: () => onBulkDelete(deletable),
            },
          ];
        }}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
            onClick: () => setShowDeleted((current) => !current),
          },
        ]}
        emptyMessage="No eligibility recorded yet."
        emptySearchMessage="No eligibility matches that search."
      />
    </ProfileSection>
  );
}
